"use client";

import { Clock } from "lucide-react";
import { Furigana } from "~/components/common/Furigana";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "~/components/ui/sidebar";
import { useHashParams } from "~/hooks/useHashParams";
import { useRunQueueStatus } from "~/hooks/useRunQueueNotifier";
import useUrlParam from "~/hooks/useUrlParam";
import logger from "~/lib/logger";
import { formatDuration } from "~/lib/utils";
import { type SelectTranscript } from "~/server/db/transcripts/schema";
import { type SelectTranslationWithTranscriptsAndLexemes } from "~/server/db/translations/schema";
import { type SelectVideo } from "~/server/db/videos/schema";

import Translation from "./Translation";
import TranslationLoading from "./TranslationLoading";
import VideoWithContent from "./VideoWithContent";

interface Props {
  video: SelectVideo;
  translation?: SelectTranslationWithTranscriptsAndLexemes;
  hasMore: boolean;
  transcripts: SelectTranscript[];
}

export default function VideoWithTranslation({
  video,
  translation,
  transcripts,
  hasMore,
}: Props) {
  const isRunning = useRunQueueStatus("process-video");
  const [, setCursor] = useUrlParam<string>("cursor", "");
  const { setHashParams } = useHashParams();
  if (!isRunning && !translation) {
    logger.info("No translation found", { videoId: video.id });
  }

  const handleTranscriptClick = (transcript: SelectTranscript) => {
    const { start, duration } = transcript;

    setCursor(start.toString());
    setHashParams({
      start: start.toString(),
      duration: duration.toString(),
    });
  };

  return (
    <SidebarProvider defaultOpen={false}>
      <div className="flex w-full">
        <div className="flex-1">
          <VideoWithContent video={video} translation={translation}>
            {translation ? (
              <Translation translation={translation} hasMore={hasMore} />
            ) : isRunning ? (
              <TranslationLoading count={5} />
            ) : (
              <TranslationLoading count={2} />
            )}
          </VideoWithContent>
        </div>

        {translation && (
          <Sidebar side="right" collapsible="icon" className="top-14">
            <SidebarHeader className="pb-0">
              <SidebarMenu>
                <SidebarMenuItem>
                  <div className="flex items-center gap-2">
                    <SidebarTrigger
                      className="aspect-square size-8 items-center justify-center"
                      icon={<Clock />}
                    />
                    <div className="flex flex-col gap-0.5 py-2 leading-none">
                      <span className="font-semibold">Transcripts</span>
                      <span className="text-xs text-muted-foreground">
                        {transcripts.length} lines
                      </span>
                    </div>
                  </div>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarHeader>
            <SidebarContent>
              <SidebarGroup className="pt-0">
                <SidebarMenu className="gap-1">
                  {transcripts.map((transcript) => (
                    <SidebarMenuItem key={transcript.id}>
                      <SidebarMenuButton
                        variant="narrow"
                        onClick={() => handleTranscriptClick(transcript)}
                        tooltip={formatDuration(transcript.start)}
                      >
                        <span className="pl-0 pt-1 font-mono text-[10px] text-muted-foreground">
                          {formatDuration(transcript.start)}
                        </span>
                        <Furigana rubyContent={transcript.textFurigana ?? ""} />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroup>
            </SidebarContent>
            <SidebarRail />
          </Sidebar>
        )}
      </div>
    </SidebarProvider>
  );
}
